/**
 * Mock Payment Page - For testing payment flow without real Stripe
 * Shows a simulated payment form that marks the invoice as paid
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CreditCard, AlertTriangle, CheckCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function MockPaymentPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Mock card number
  const [cardNumber, setCardNumber] = useState('4242 4242 4242 4242');
  
  const sessionId = searchParams.get('session');

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchInvoice();
    }
  }, [invoiceId, authLoading, isAuthenticated]);

  const fetchInvoice = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/invoices/${invoiceId}`);
      if (!response.ok) throw new Error('Invoice not found');
      const data = await response.json();
      setInvoice(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMockPayment = async () => {
    setProcessing(true);
    setError(null);
    
    // Simulate payment processing delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    try {
      // Mark invoice as paid via admin endpoint (mock)
      const response = await authFetch(`${API_BASE}/api/invoices/${invoiceId}/mark-paid`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to process payment');
      }
      
      setSuccess(true);
      
      // Redirect to gallery after 2 seconds
      setTimeout(() => {
        if (invoice?.order_id) {
          // Try to get gallery slug from order
          navigate(`/orders`);
        } else {
          navigate(`/invoice/${invoiceId}?paid=true`);
        }
      }, 2000);
      
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <p className="text-white">Please log in to continue</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Card className="bg-[#0f1724] border-green-500/30 max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl text-white font-light mb-2">Payment Successful!</h2>
            <p className="text-[#E8E4DE]/60">Redirecting to your orders...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Test Mode Banner */}
        <Card className="bg-yellow-500/10 border-yellow-500/30 mb-6">
          <CardContent className="py-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
            <div>
              <p className="text-yellow-400 font-medium">Test Mode</p>
              <p className="text-yellow-400/70 text-sm">
                This is a simulated payment. Use test card 4242 4242 4242 4242.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Payment Form */}
        <Card className="bg-[#0f1724] border-[#C6A14A]/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#C6A14A]" />
              Mock Payment
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Invoice {invoice?.invoice_number}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Amount */}
            <div className="bg-[#C6A14A]/10 rounded-lg p-4 text-center">
              <span className="text-[#E8E4DE]/60 text-sm">Amount Due</span>
              <p className="text-3xl text-[#C6A14A] font-semibold">
                ${invoice ? (invoice.total_cents / 100).toFixed(2) : '0.00'}
              </p>
            </div>

            {/* Card Number */}
            <div className="space-y-2">
              <Label className="text-[#E8E4DE]/70">Card Number</Label>
              <Input
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                className="bg-[#050816] border-white/10 text-white"
                placeholder="4242 4242 4242 4242"
              />
            </div>

            {/* Expiry & CVC */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#E8E4DE]/70">Expiry</Label>
                <Input
                  defaultValue="12/28"
                  className="bg-[#050816] border-white/10 text-white"
                  placeholder="MM/YY"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#E8E4DE]/70">CVC</Label>
                <Input
                  defaultValue="123"
                  className="bg-[#050816] border-white/10 text-white"
                  placeholder="123"
                />
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}

            {/* Pay Button */}
            <Button
              className="w-full bg-[#C6A14A] hover:bg-[#d4af5a] text-black font-semibold h-12"
              onClick={handleMockPayment}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                `Pay $${invoice ? (invoice.total_cents / 100).toFixed(2) : '0.00'}`
              )}
            </Button>

            <p className="text-[#E8E4DE]/40 text-xs text-center">
              Secured by RC Studios Mock Payment Gateway
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
